/**
 * WelcomeBanner Component
 *
 * Shown on the dashboard for users who recently completed onboarding.
 * Provides a warm welcome and quick-start guidance.
 * Dismissible via localStorage flag.
 */

import { useState } from "react";
import { X, PartyPopper } from "lucide-react";

const DISMISS_KEY = "attunewell_welcome_banner_dismissed";

interface WelcomeBannerProps {
  firstName: string | null;
}

export function WelcomeBanner({ firstName }: WelcomeBannerProps) {
  const [dismissed, setDismissed] = useState(
    () => localStorage.getItem(DISMISS_KEY) === "true"
  );

  if (dismissed) return null;

  const handleDismiss = () => {
    localStorage.setItem(DISMISS_KEY, "true");
    setDismissed(true);
  };

  return (
    <div className="relative rounded-lg border border-primary/20 bg-primary/5 p-6 mb-8">
      <button
        onClick={handleDismiss}
        className="absolute top-3 right-3 text-muted-foreground hover:text-foreground"
        aria-label="Dismiss welcome banner"
      >
        <X className="w-4 h-4" />
      </button>
      <div className="flex items-start gap-4">
        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
          <PartyPopper className="w-5 h-5 text-primary" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-foreground">
            Welcome to AttuneWell{firstName ? `, ${firstName}` : ""}!
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Your account is set up and ready to go. Upload a conversation
            recording to get your first AI-powered insights, or try the
            relationship tracker to start logging daily entries.
          </p>
        </div>
      </div>
    </div>
  );
}
